import PropTypes from 'prop-types';
import { Typography } from 'antd';
import { IconInbox } from '../icons';
import IconTile from './IconTile';
import './primitives.css';

const { Title, Text } = Typography;

/**
 * EmptyState
 *
 * An empty screen is a screen that has to say what to do next, so `action` is
 * part of the contract — see the spec template in `docs/design-workflow.md`
 * ("Empty: copy + the action that fills it"). A page that says "no data" and
 * offers no way to create any is a dead end, and it is the state an owner is
 * most likely to meet first in a demo, on a centre with no data in it yet.
 *
 * The glyph sits in an IconTile rather than alone in the middle of nothing
 * (`design-language.md` §5), and `illustration` replaces the tile entirely when
 * a screen has something better to show.
 *
 * Sizes:
 *   - sm: inline (inside a card, or the resting state of a control) — the one
 *         case where an action is not required, because the control is one
 *   - md: page-level (default)
 *   - lg: hero / first-run
 */

/* One constant message, so a run of the app logs this once rather than once per
   screen. The list of screens that still owe an action is in FOUND-BUGS.md. */
const ACTION_REQUIRED_MESSAGE =
  'EmptyState: `action` is required at size "md" and "lg" — docs/design-workflow.md § Spec ' +
  'template, "Empty: copy + the action that fills it". Pass the control that fills the screen, ' +
  'or size="sm" if this is an inline state of a control that is itself the action.';

const requiredAction = (props, propName) => {
  if (props.size === 'sm') return null;
  const value = props[propName];
  if (value === undefined || value === null || value === false) {
    return new Error(ACTION_REQUIRED_MESSAGE);
  }
  return null;
};

const EmptyState = ({
  icon = <IconInbox />,
  illustration,
  tone = 'neutral',
  title,
  description,
  action,
  size = 'md',
  className = '',
}) => (
  <div
    className={['lms-empty-state', `lms-empty-state--${size}`, 'lms-motif', className]
      .filter(Boolean)
      .join(' ')}
    role="status"
    aria-live="polite"
  >
    {illustration ? (
      <div className="lms-empty-state__illustration">{illustration}</div>
    ) : (
      icon && (
        <IconTile
          icon={icon}
          tone={tone}
          size={size === 'sm' ? 'md' : 'lg'}
          className="lms-empty-state__icon"
        />
      )
    )}
    {title && (
      <Title level={size === 'sm' ? 5 : 4} className="lms-empty-state__title">
        {title}
      </Title>
    )}
    {description && (
      <Text type="secondary" className="lms-empty-state__description">
        {description}
      </Text>
    )}
    {action && <div className="lms-empty-state__action">{action}</div>}
  </div>
);

EmptyState.propTypes = {
  icon: PropTypes.node,
  /** Takes the tile's place when a screen has real art for its empty state. */
  illustration: PropTypes.node,
  tone: IconTile.propTypes.tone,
  title: PropTypes.node,
  description: PropTypes.node,
  /** The control that fills the screen. Required except at size="sm". */
  action: requiredAction,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

export default EmptyState;
